import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Modal, ModalHeader, ModalBody, SIZE } from "baseui/modal";
import {
  StatefulDataTable,
  StringColumn,
  NumericalColumn,
} from "baseui/data-table";

const InitInspectsModal = ({
  isModalOpen,
  inspections,
  steps,
  users,
  locations,
  onClose,
  matchingInitRows,
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [initInspectData, setInitInspectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [mappedData, setMappedData] = useState([]);
  const inv_id = useMemo(
    () => matchingInitRows[0]?.inventory_id,
    [matchingInitRows]
  );

  function formatDate(dateString) {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const time = `${hours}:${minutes}${ampm}`;
    return `${month}-${day}-${year}, ${time}`;
  }

  useEffect(() => {
    if (inv_id && isModalOpen && !initInspectData) {
      // Prevent infinite loop by checking if data already exists
      const fetchInitInspectData = async () => {
        try {
          setLoading(true);
          const response = await axios.get(
            `${apiUrl}/api/full_init_inspect/${inv_id}`
          );
          setInitInspectData(response.data);
        } catch (error) {
          console.error("Error fetching initial inspection data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchInitInspectData();
    }
  }, [inv_id, isModalOpen, apiUrl, initInspectData]);

  const relatedInspections = useMemo(() => {
    const filteredInspections = inspections.filter(
      (insp) => insp.inventory_id === inv_id
    );

    let pauseStarted = false; // Flag to track if a "pause" state was encountered
    let pauseEnded = false; // Flag to track if an "unpause" state was encountered
    let pausedInspections = []; // Array to store inspections during the pause

    return filteredInspections.filter((insp) => {
      // Exclude any inspections with step_id of 11, 12, 13, or 14
      if (
        insp.step_id === 11 ||
        insp.step_id === 12 ||
        insp.step_id === 13 ||
        insp.step_id === 14
      ) {
        return false;
      }

      // Always include inspections with step_id === 15
      if (insp.step_id === 15) {
        return true;
      }

      // Detect pause starting: Init section moving to non-init
      if (
        (insp.step_id === 11 ||
          insp.step_id === 12 ||
          insp.step_id === 13 ||
          insp.step_id === 14) &&
        insp.next_status !== 11 &&
        insp.next_status !== 12 &&
        insp.next_status !== 13 &&
        insp.next_status !== 14 &&
        insp.next_status !== 15
      ) {
        pauseStarted = true; // Start the pause period without including this inspection
        return false; // Exclude the inspection that initiates the pause
      }

      // Detect pause ending: Non-init section moving back to init
      if (
        insp.step_id !== 11 &&
        insp.step_id !== 12 &&
        insp.step_id !== 13 &&
        insp.step_id !== 14 &&
        insp.step_id !== 15 &&
        (insp.next_status === 11 ||
          insp.next_status === 12 ||
          insp.next_status === 13 ||
          insp.next_status === 14)
      ) {
        pauseEnded = true; // End the pause period
        pausedInspections.push(insp); // Add the current inspection to paused list

        // Include all inspections during the pause period, omitting the initial "pause start" inspection
        const inspectionsDuringPause = [...pausedInspections];
        pausedInspections = []; // Clear the array for the next pause cycle
        pauseStarted = false;
        pauseEnded = false;

        return inspectionsDuringPause;
      }

      // During pause: Add inspections to the paused list if pause started but not ended
      if (pauseStarted && !pauseEnded) {
        pausedInspections.push(insp);
        return true;
      }

      // By default, exclude inspections if none of the conditions are met
      return false;
    });
  }, [inspections, inv_id]);

  console.log("relatedInspections", relatedInspections);

  const sortedByDate = useMemo(() => {
    // Combine matchingInitRows and relatedInspections into one array
    const combinedRows = [...matchingInitRows, ...relatedInspections];

    console.log("combinedRows.length", combinedRows.length);

    // Sort the combined array by converting the inspection_date back to a Date object
    const sortedRows = combinedRows.sort((a, b) => {
      const dateA = new Date(a.inspection_date);
      const dateB = new Date(b.inspection_date);

      // Check if dateA or dateB is invalid
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        console.warn(
          "Invalid date detected:",
          a.inspection_date,
          b.inspection_date
        );
        return 0; // Consider them equal for sorting purposes
      }

      return dateA - dateB; // Sort by date
    });

    // Log sorted rows for debugging
    console.log("sortedRows:", sortedRows);
    return sortedRows;
  }, [matchingInitRows, relatedInspections]);

  console.log("sortedByDate", sortedByDate); // inspection objects w/out date fomatting, ordered correctly
  const formattedRelatedInspections = relatedInspections.map(
    (inspection) => ({
      ...inspection,
      inspection_date: formatDate(inspection.inspection_date),
      inspector: users[inspection.inspector_id] || "n/a",
      vehicle: matchingInitRows[0].vehicle,
      location: locations[inspection.location],
      // init_inspect: steps[inspection.step_id]?.toString() || "n/a",
      init_inspect: inspection.id,
      currentSection: steps[inspection.step_id]
        ? steps[inspection.step_id] +
          (steps[inspection.step_id] === "init_return"
            ? " (RETURN)"
            : " (PAUSE)")
        : "n/a",

      vehicle_id: inspection.inventory_id,
    })
  );
  useEffect(() => {
    if (initInspectData && initInspectData.length > 0) {
      //For relatedInspections
      
      console.log("formattedRelatedInspections", formattedRelatedInspections);

      const distributeProperties = (
        initInspectData,
        sortedByDate,
        formattedRelatedInspections
      ) => {
        const initInspectMap = initInspectData.reduce((map, item) => {
          map[item.id] = item;
          return map;
        }, {});

        console.log("sortedByDate", sortedByDate);

        return sortedByDate.map((inspection) => {
          // Apply formatDate to inspection_date for sortedByDate objects
          const formattedDate = formatDate(inspection.inspection_date);

          // Check if the inspection has an init_inspect
          if (inspection.init_inspect) {
            return {
              ...inspection,
              inspection_date: formattedDate, // Use the formatted date here
              ...(initInspectMap[inspection.init_inspect] || {}),
            };
          } else {
            // If init_inspect is missing, find matching inspection in formattedRelatedInspections
            const relatedInspection = formattedRelatedInspections.find(
              (formatted) => formatted.id === inspection.id
            );

            // Return the combined object, preferring the properties from the formatted related inspection
            return {
              ...inspection,
              inspection_date: formattedDate, // Use the formatted date here
              ...(relatedInspection || {}), // Fallback to an empty object if not found
            };
          }
        });
      };

      console.log("initInspectData", initInspectData);

      // Update combinedData to use distributeProperties as before
      const combinedData = [
        ...distributeProperties(
          initInspectData,
          sortedByDate,
          formattedRelatedInspections
        ),
      ];

      // console.log("combinedData", combinedData);

      const finalSortedData = combinedData.sort(
        (a, b) => new Date(a.inspection_date) - new Date(b.inspection_date)
      );

      const allFields = [
        ...new Set([
          ...Object.keys(sortedByDate[0] || {}),
          ...Object.keys(initInspectData[0] || {}).slice(1),
        ]),
      ];

      console.log("allFields", allFields);

      const createColumns = () => {
        return allFields
          .map((field, index) => {
            const sampleValue = finalSortedData[0][field];

            // Define default width for some columns (optional)
            const inspectionDateWidth = 700; // Example width in pixels

            // Skip the vehicle field
            if (
              field === "inventory_id" ||
              field === "vehicle" ||
              field === "stock" ||
              field === "vehicle_id"
            ) {
              return null; // Skip this field
            }
            // if (field === "inventory_id") {
            //   return StringColumn({
            //     title: "inv_id",
            //     mapDataToValue: (row) => row[field] || "",
            //     width: inspectionDateWidth, // Set the width for the inspection_date column
            //   });
            // }

            if (field === "description") {
              // Define special columns
              return StringColumn({
                title: field,
                mapDataToValue: (row) => row[field] || "",
                maxWidth: 300, // Set the width for the inspection_date column

                whiteSpace: "normal", // Enable text wrapping
                overflowWrap: "break-word",
              });
            }

            if (field === "inspection_date") {
              // Define special columns
              return StringColumn({
                title: field,
                mapDataToValue: (row) => row[field] || "",
                minWidth: 150, // Set the width for the inspection_date column
              });
            }
            if (field === "inspector") {
              return StringColumn({
                title: field,
                mapDataToValue: (row) => row[field] || "",
                minWidth: 110, // Set the width for the inspection_date column
              });
            }

            if (field === "init_inspect") {
              return StringColumn({
                title: "id",
                mapDataToValue: (row) => row[field] || "",
                width: inspectionDateWidth, // Set the width for the inspection_date column
              });
            }

            // Add the "description" column after the "currentSection" column
            if (field === "currentSection") {
              // Return the "currentSection" column
              const currentSectionColumn = StringColumn({
                minWidth: 170,
                title: field,
                mapDataToValue: (row) => row[field] || "",
              });

              return [currentSectionColumn];
            }

            // Handle number fields
            if (typeof sampleValue === "number") {
              return NumericalColumn({
                title: field,
                mapDataToValue: (row) => row[field] || 0,
              });
            }

            // Handle all other fields as string columns
            return StringColumn({
              title: field,
              mapDataToValue: (row) => row[field] || "",
            });
          })
          .flat() // Flatten the array to handle the insertion of both `currentSection` and `description`
          .filter(Boolean); // Filter out any null values
      };

      setColumns(createColumns());
      setMappedData(finalSortedData);
    }
  }, [initInspectData, sortedByDate, relatedInspections, steps, formattedRelatedInspections]); // eslint-disable-next-line react-hooks/exhaustive-deps

  console.log("mappedData", mappedData);

  return (
    <Modal isOpen={isModalOpen} onClose={onClose} size={SIZE.full} closeable>
      <ModalHeader>
        <span
          style={{ fontSize: "1.5em", fontWeight: "500" }}
        >{`Initial Inspections: `}</span>
        <br />
        <br />
        <span style={{ color: "blue" }}>
          {`${matchingInitRows[0]?.vehicle}  ${" "}  SN: ${
            matchingInitRows[0]?.stock
          }  ${" "}  ID: ${matchingInitRows[0]?.inventory_id}`}
        </span>
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div style={{ height: "85vh", width: "97vw" }}>
            <StatefulDataTable
              rowHeight={80}
              columns={columns}
              rows={mappedData.map((data, index) => ({ id: index, data }))}
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default InitInspectsModal;
